import { z } from "zod";
import { createEndpoint, StandardSuccessResponseSchema } from "../createEndpoint";
import { HttpClient, QueryParams } from "../httpClient";
import {
    DraftLeadPublicResponseSchema,
    CheckAuthResponseSchema,
    SubmitDraftLeadResponseSchema,
    CreateOrUpdateDraftLeadRequest,
} from "../schemas/draft-leads";

export function createPublicDraftLeadsApi(http: HttpClient, baseUrl: string) {
    const publicBaseUrl = `${baseUrl}/public/draft-leads`;

    return {
        /**
         * Create a new draft lead or update an existing one using a token
         * POST /api/v2/public/draft-leads
         * 
         * - When creating: provide email (required)
         * - When updating: provide token (required)
         */
        createOrUpdate: createEndpoint<
            z.infer<typeof DraftLeadPublicResponseSchema>,
            CreateOrUpdateDraftLeadRequest,
            QueryParams
        >(http, "POST", "", {
            base: publicBaseUrl,
            responseSchema: DraftLeadPublicResponseSchema,
        }),

        /**
         * Get a draft lead by its token
         * GET /api/v2/public/draft-leads/:token
         */
        getByToken: createEndpoint<
            z.infer<typeof DraftLeadPublicResponseSchema>,
            unknown,
            QueryParams,
            { token: string }
        >(http, "GET", "/:token", {
            base: publicBaseUrl,
            responseSchema: DraftLeadPublicResponseSchema,
            params: ["token"],
        }),

        /**
         * Check if the email in the draft belongs to an existing user account
         * GET /api/v2/public/draft-leads/:token/check-auth
         * 
         * Returns { needs_auth: true } if email belongs to existing account
         * Returns { needs_auth: false } if new email
         */
        checkAuth: createEndpoint<
            z.infer<typeof CheckAuthResponseSchema>,
            unknown,
            QueryParams,
            { token: string }
        >(http, "GET", "/:token/check-auth", {
            base: publicBaseUrl,
            responseSchema: CheckAuthResponseSchema,
            params: ["token"],
        }),

        /**
         * Submit a complete draft lead and convert it to an actual lead
         * POST /api/v2/public/draft-leads/:token/submit
         * 
         * - No auth needed if email is new (not in system)
         * - Auth required if email belongs to existing user
         */
        submit: createEndpoint<
            z.infer<typeof SubmitDraftLeadResponseSchema>,
            unknown,
            QueryParams,
            { token: string }
        >(http, "POST", "/:token/submit", {
            base: publicBaseUrl,
            responseSchema: SubmitDraftLeadResponseSchema,
            params: ["token"],
        }),
    };
}

export type PublicDraftLeadsApi = ReturnType<typeof createPublicDraftLeadsApi>;
